// ABOUTME: Utility functions for order variation code feature (RMC only).
// Provides helpers to determine visual treatments, i18n descriptions, and banner content per variation code.

import {TFunction} from 'i18next'
import moment from 'moment'

import {timeFormatter} from '@hconnect/uikit'

import {Order} from './Order.types'

/**
 * Shared danger color for variation code date highlighting
 */
export const VARIATION_CODE_DANGER_COLOR = '#D32F2F'

/**
 * Shared caption style for variation code description subtexts
 */
export const variationCodeCaptionStyle = {
  color: 'rgba(0, 55, 77, 0.6)',
  display: 'block' as const,
  marginTop: 4
}

/**
 * All known variation codes
 */
export type VariationCode =
  | 'RE'
  | 'RL'
  | 'RN'
  | 'SE'
  | 'SL'
  | 'UE'
  | 'CC'
  | 'DF'
  | 'G-'
  | 'G+'
  | 'MD'

const DATE_STRIKETHROUGH_CODES = new Set(['DF', 'G-', 'G+'])
const DESTINATION_CODES = new Set(['MD'])
const TOP_BANNER_CODES = new Set(['UE', 'CC'])
const INLINE_BANNER_CODES = new Set(['DF', 'G-', 'G+'])

/**
 * Whether the order has a variation code set
 */
export const hasVariationCode = (order: Order): boolean =>
  !!order.variationCode && order.variationCode.trim().length > 0

/**
 * Whether the variation code is one of the "unconfirmed" codes (UE / CC) whose booked delivery time
 * is rescheduled on confirmation. These are the only codes that drive the confirm flow.
 *
 * NOTE: mirrored by `isUeCcVariationCode` in
 * packages/tuc-processes/src/orderManagement/orderChange/variationCode/getUeCcVariationCodeAdjustedTime.ts.
 * Keep the UE/CC set in sync across both copies.
 */
export const isUeCcVariationCode = (code?: string | null): boolean =>
  code === 'UE' || code === 'CC'

/**
 * Whether the ISO 8601 duration offset represents a real (non-zero) reschedule. The zero sentinel can
 * appear as 'PT0S' / 'P0D' and still be a truthy string, so we check the actual magnitude.
 *
 * NOTE: mirrored by `hasNonZeroOffset` in
 * packages/tuc-processes/src/orderManagement/orderChange/variationCode/getUeCcVariationCodeAdjustedTime.ts.
 * Keep the zero-offset semantics in sync across both copies.
 */
const hasNonZeroVariationOffset = (offset?: string): boolean => {
  if (!offset) return false
  const duration = moment.duration(offset)
  return duration.isValid() && duration.asMilliseconds() !== 0
}

/**
 * Whether an unconfirmed UE/CC order's rescheduled delivery time (`variationCodeAdjustedTime`) has
 * already passed. Once it has, the order can no longer be confirmed in-app and the customer must
 * contact dispatch. Only applies to UE/CC codes with a non-zero offset and a valid adjusted time;
 * every other case (other codes, zero offset, missing/invalid adjusted time) returns false.
 */
export const isVariationCodeConfirmationExpired = (
  order: Order,
  now: moment.Moment = moment()
): boolean => {
  const {variationCode, variationCodeOffset, variationCodeAdjustedTime} = order
  if (
    !isUeCcVariationCode(variationCode) ||
    !variationCodeAdjustedTime ||
    !hasNonZeroVariationOffset(variationCodeOffset)
  ) {
    return false
  }
  const adjusted = moment(variationCodeAdjustedTime)
  return adjusted.isValid() && now.isAfter(adjusted)
}

/**
 * Whether the variation code requires the requested date to be rendered in danger/red color
 */
export const isDateDangerCode = (code: string): boolean => TOP_BANNER_CODES.has(code)

/**
 * Whether the variation code requires the requested date to be rendered with strikethrough
 * and an estimated arrival time shown alongside
 */
export const isDateStrikethroughCode = (code: string): boolean =>
  DATE_STRIKETHROUGH_CODES.has(code)

/**
 * Whether the variation code requires the requested date to be rendered with strikethrough only when the offset is not zero, indicating a non-immediate reschedule
 */
export const isDateStrikethroughWhenOffsetNotZero = (code: string): boolean =>
  TOP_BANNER_CODES.has(code)

/**
 * Whether the variation code applies to the destination field instead of the requested date
 */
export const isDestinationCode = (code: string): boolean => DESTINATION_CODES.has(code)

/**
 * Whether the variation code has a top-of-panel banner (UE, CC)
 */
export const isTopBannerCode = (code: string): boolean => TOP_BANNER_CODES.has(code)

/**
 * Whether the variation code has an inline banner below the date row (DF, G-, G+)
 */
export const isInlineBannerCode = (code: string): boolean => INLINE_BANNER_CODES.has(code)

/**
 * Whether the variation code has any banner (top or inline)
 */
export const hasBanner = (code: string): boolean =>
  isTopBannerCode(code) || isInlineBannerCode(code)

/**
 * Returns the banner variant for top-of-panel banners.
 * UE → always 'error'. CC → 'warning' when offset ≠ PT0S, 'error' when offset = PT0S.
 */
export const getTopBannerVariant = (code: string, offsetValue: string): 'error' | 'warning' | null => {
  if (!TOP_BANNER_CODES.has(code)) return null
  if (code === 'CC' && offsetValue !== 'PT0S') return 'warning'
  return 'error'
}

/**
 * Returns the description message for the variation code, to be shown as subtext.
 * Returns null for codes that don't have a description (DF, G-, G+).
 */
export const getVariationCodeDescription = (
  order: Order,
  t: TFunction,
  language?: string,
  eventLocationOffset?: number
): string | null => {
  const {variationCode, variationCodeAdjustedTime, variationCodeOffset} = order
  if (!variationCode || !variationCodeOffset) return null

  const offsetValue = formatTimespanOffset(variationCodeOffset, t)  
  switch (variationCode) {
    case 'RE': {
      const formattedTime = variationCodeAdjustedTime
        ? timeFormatter(variationCodeAdjustedTime, language, eventLocationOffset)
        : ''
      return t('order.variationCode.RE.description', {preferredTime: formattedTime})
    }

    case 'RL': {
      const formattedTime = variationCodeAdjustedTime
        ? timeFormatter(variationCodeAdjustedTime, language, eventLocationOffset)
        : ''
      return t('order.variationCode.RL.description', {preferredTime: formattedTime})
    }

    case 'RN':
      return t('order.variationCode.RN.description', {
        offset: variationCodeOffset ? formatTimespanOffset(variationCodeOffset, t) : ''
      })

    case 'SE': {
      const rescheduledSource =
        order.variationCodeRescheduledFrom ?? order.schedules?.[0]?.scheduleStartDate
      const formattedTime = rescheduledSource
        ? timeFormatter(rescheduledSource, language, eventLocationOffset)
        : ''
      return t('order.variationCode.SE.description', {originalTime: formattedTime})
    }

    case 'SL': {
      const rescheduledSource =
        order.variationCodeRescheduledFrom ?? order.schedules?.[0]?.scheduleStartDate
      const formattedTime = rescheduledSource
        ? timeFormatter(rescheduledSource, language, eventLocationOffset)
        : ''
      return t('order.variationCode.SL.description', {originalTime: formattedTime})
    }

    case 'UE':
    case 'CC':
      if (offsetValue === 'PT0S') return t('order.variationCode.UE.description')
      else return t('order.variationCode.CC.description')

    case 'MD':
      return t('order.variationCode.MD.description')

    // DF, G-, G+ have no description subtext — they use inline banners instead
    default:
      return null
  }
}

/**
 * Returns top-of-panel banner content for UE/CC codes.
 * Returns null for all other codes.
 */
export const getTopBannerContent = (
  order: Order,
  t: TFunction
): {title: string; description: string; variant: 'error' | 'warning'} | null => {
  const {variationCode, variationCodeOffset} = order
  
  if (!variationCode || !variationCodeOffset || !isTopBannerCode(variationCode)) return null

  const offsetValue = formatTimespanOffset(variationCodeOffset, t)
  const variant = getTopBannerVariant(variationCode, offsetValue)
  if (!variant) return null

  const isAtRisk = offsetValue === 'PT0S'
  const bannerKey = isAtRisk ? 'UE' : 'CC'
  const title = t(`order.variationCode.${bannerKey}.banner.title`)
  const description = t(`order.variationCode.${bannerKey}.banner.description`)

  return {title, description, variant}
}

/**
 * Formats an ISO 8601 duration (e.g. 'PT45M', 'PT1H30M') into a localized human-readable duration.
 * Returns e.g. '45 min', '1 hr 30 min', '2 hr' (English) with proper i18n support.
 */
export const formatTimespanOffset = (offset: string, t: TFunction): string => {
  const duration = moment.duration(offset)
  const hours = Math.floor(duration.asHours())
  const minutes = duration.minutes()

  if (hours > 0 && minutes > 0)
    return t('order.variationCode.durationHoursAndMinutes', {hours, minutes})
  if (hours > 0) return t('order.variationCode.durationHoursOnly', {hours})
  if (minutes > 0) return t('order.variationCode.durationMinutesOnly', {minutes})
  return offset
}

/**
 * Returns inline banner content for DF/G-/G+ codes.
 * Returns null for all other codes.
 */
export const getInlineBannerContent = (
  order: Order,
  t: TFunction
): {description: string; variant: 'error' | 'info'} | null => {
  const {variationCode, variationCodeOffset} = order
  if (!variationCode || !isInlineBannerCode(variationCode)) return null

  const formattedOffset = variationCodeOffset ? formatTimespanOffset(variationCodeOffset, t) : ''

  let description: string
  switch (variationCode) {
    case 'DF':
      description = t('order.variationCode.DF.banner.description', {offset: formattedOffset})
      break
    case 'G-':
      description = t('order.variationCode.G-.banner.description', {offset: formattedOffset})
      return {description, variant: 'info'}
    case 'G+':
      description = t('order.variationCode.G+.banner.description', {offset: formattedOffset})
      break
    default:
      return null
  }

  return {
    description,
    variant: 'error'
  }
}
